import type {
  HygieneTaskProduction,
  THygieneTaskCode,
  THygieneTaskType,
} from '@/react-query/insights/types';

import {
  type THygieneRates,
  type THygieneTaskPeriod,
  type THygieneTasks,
  TYPE_TO_TASK_CODE,
} from '../types';

import { taskCodesToTypes } from './taskTypeHelpers';

interface HygieneTotals {
  currentTasks: number;
  previousTasks: number;
}

interface HygieneTaskData {
  production: { [key: string]: THygieneTaskPeriod };
  opportunity: { [key: string]: THygieneTaskPeriod };
}

type HygieneTaskPeriods = {
  production: { [key in THygieneTaskType]: THygieneTaskPeriod };
  opportunity: { [key in THygieneTaskType]: THygieneTaskPeriod };
};

export const buildHygieneTaskPeriods = (
  tasks: { [key: string]: HygieneTaskProduction },
  estimatedValues: { [key in THygieneTaskType]: { value: number } },
  isCounts?: boolean,
): HygieneTaskPeriods => {
  return {
    production: convertTasksToTaskPeriods(tasks, isCounts),
    opportunity: convertTasksToOpportunityPeriods(
      tasks,
      estimatedValues,
      isCounts,
    ),
  };
};

export const calculateHygieneRatesFromData = (
  data: HygieneTaskData,
  estimatedHygieneValues: { [key in THygieneTaskType]: { value: number } },
): THygieneRates => {
  const productionTasks = calculateHygieneTasks(data.production);

  const opportunityTasks = calculateHygieneTasks(data.opportunity);

  const productionTotals = calculateTotalsFromTasks(productionTasks.tasks);
  const opportunityTotals = calculateTotalsFromTasks(opportunityTasks.tasks);

  const { productionRatio, productionRatioChange } = calculateProductionRatio(
    productionTotals,
    opportunityTotals,
  );

  return {
    production: {
      ...productionTasks,
      overallTasks: productionTotals.currentTasks,
      overallTasksChange:
        (productionTotals.currentTasks - productionTotals.previousTasks) /
        productionTotals.previousTasks,
    },
    opportunity: {
      ...opportunityTasks,
      overallTasks: opportunityTotals.currentTasks,
      overallTasksChange:
        (opportunityTotals.currentTasks - opportunityTotals.previousTasks) /
        opportunityTotals.previousTasks,
    },
    estimatedHygieneValues,
    productionRatio,
    productionRatioChange,
  };
};

// Calculate hygiene rates for a given period
const calculateRateChange = (
  current: THygieneTaskPeriod['current'],
  previous: THygieneTaskPeriod['previous'],
): {
  rateChange: number;
} => {
  const currentTasks = current || 0;

  const previousTasks = previous || 0;

  return {
    rateChange: (currentTasks - previousTasks) / previousTasks,
  };
};

const calculateProductionRatio = (
  productionTotals: HygieneTotals,
  opportunityTotals: HygieneTotals,
) => {
  const productionValue = productionTotals.currentTasks;
  const opportunityValue = opportunityTotals.currentTasks;

  const previousProductionValue = productionTotals.previousTasks;
  const previousOpportunityValue = opportunityTotals.previousTasks;

  const currentRatio =
    opportunityValue + productionValue > 0
      ? productionValue / (opportunityValue + productionValue)
      : 0;
  const previousRatio =
    previousOpportunityValue + previousProductionValue > 0
      ? previousProductionValue /
        (previousOpportunityValue + previousProductionValue)
      : 0;

  return {
    productionRatio: currentRatio,
    productionRatioChange: (currentRatio - previousRatio) / previousRatio,
  };
};

export const calculateOverallHygieneRates = (
  tasks?: THygieneRates[],
  enabledTaskTypes?: THygieneTaskType[],
): THygieneRates => {
  if (!tasks?.length || !enabledTaskTypes) {
    return {
      production: {
        tasks: createEmptyTasks(enabledTaskTypes ?? []),
      },
      opportunity: {
        tasks: createEmptyTasks(enabledTaskTypes ?? []),
      },
      estimatedHygieneValues: {} as {
        [key in THygieneTaskType]: { value: number };
      },
    };
  }

  const productionAggregated = createEmptyTasks(enabledTaskTypes);
  const opportunityAggregated = createEmptyTasks(enabledTaskTypes);
  const productionTotals: HygieneTotals = {
    currentTasks: 0,
    previousTasks: 0,
  };
  const opportunityTotals: HygieneTotals = {
    currentTasks: 0,
    previousTasks: 0,
  };

  tasks.forEach((providerRates) => {
    // Aggregate production tasks
    Object.entries(providerRates.production.tasks).forEach(
      ([taskType, taskPeriod]) => {
        const current =
          productionAggregated[taskType as THygieneTaskType].current;
        const previous =
          productionAggregated[taskType as THygieneTaskType].previous;

        // Update aggregated values
        productionAggregated[taskType as THygieneTaskType].current +=
          taskPeriod.current || 0;
        productionAggregated[taskType as THygieneTaskType].previous +=
          taskPeriod.previous || 0;

        // Update totals
        productionTotals.currentTasks += taskPeriod.current || 0;
        productionTotals.previousTasks += taskPeriod.previous || 0;

        productionAggregated[taskType as THygieneTaskType].rateChange =
          (current - previous) / previous;
      },
    );

    // Aggregate opportunity tasks
    Object.entries(providerRates.opportunity.tasks).forEach(
      ([taskType, taskPeriod]) => {
        const current =
          opportunityAggregated[taskType as THygieneTaskType].current;
        const previous =
          opportunityAggregated[taskType as THygieneTaskType].previous;

        // Update aggregated values
        opportunityAggregated[taskType as THygieneTaskType].current +=
          taskPeriod.current || 0;
        opportunityAggregated[taskType as THygieneTaskType].previous +=
          taskPeriod.previous || 0;

        // Update totals
        opportunityTotals.currentTasks += taskPeriod.current || 0;
        opportunityTotals.previousTasks += taskPeriod.previous || 0;

        // Calculate rate change
        opportunityAggregated[taskType as THygieneTaskType].rateChange =
          (current - previous) / previous;
      },
    );
  });

  // Calculate production ratio
  const currentProductionRatio =
    opportunityTotals.currentTasks + productionTotals.currentTasks > 0
      ? productionTotals.currentTasks /
        (opportunityTotals.currentTasks + productionTotals.currentTasks)
      : 0;
  const previousProductionRatio =
    opportunityTotals.previousTasks + productionTotals.previousTasks > 0
      ? productionTotals.previousTasks /
        (opportunityTotals.previousTasks + productionTotals.previousTasks)
      : 0;

  return {
    production: {
      tasks: productionAggregated,
      overallTasks: productionTotals.currentTasks,
      overallTasksChange:
        (productionTotals.currentTasks - productionTotals.previousTasks) /
        productionTotals.previousTasks,
    },
    opportunity: {
      tasks: opportunityAggregated,
      overallTasks: opportunityTotals.currentTasks,
      overallTasksChange:
        (opportunityTotals.currentTasks - opportunityTotals.previousTasks) /
        opportunityTotals.previousTasks,
    },
    estimatedHygieneValues: tasks[0].estimatedHygieneValues,
    productionRatio: currentProductionRatio,
    productionRatioChange:
      (currentProductionRatio - previousProductionRatio) /
      previousProductionRatio,
  };
};

const calculateTotalsFromTasks = (tasks: {
  [key in THygieneTaskType]: THygieneTaskPeriod;
}): HygieneTotals => {
  return {
    currentTasks: Object.values(tasks).reduce(
      (sum, task) => sum + task.current,
      0,
    ),
    previousTasks: Object.values(tasks).reduce(
      (sum, task) => sum + task.previous,
      0,
    ),
  };
};

// Calculate hygiene tasks for a given period
export const calculateHygieneTasks = (tasks: {
  [key: string]: THygieneTaskPeriod;
}): THygieneTasks => {
  const hygieneTasks: THygieneTasks = {
    tasks: createEmptyTasks(Object.keys(tasks) as THygieneTaskType[]),
    overallTasks: 0,
    overallTasksChange: 0,
  };

  const totals: HygieneTotals = {
    currentTasks: 0,
    previousTasks: 0,
  };

  Object.entries(tasks).forEach(([taskType, taskPeriod]) => {
    const { rateChange } = calculateRateChange(
      taskPeriod.current,
      taskPeriod.previous,
    );

    // Update totals
    totals.currentTasks += taskPeriod.current || 0;
    totals.previousTasks += taskPeriod.previous || 0;

    // Create a new object that matches THygieneTaskPeriod
    const periodWithRateChange: THygieneTaskPeriod = {
      current: taskPeriod.current,
      previous: taskPeriod.previous,
      rateChange,
    };

    hygieneTasks.tasks[taskType as THygieneTaskType] = periodWithRateChange;
  });

  return {
    ...hygieneTasks,
    overallTasks: totals.currentTasks,
    overallTasksChange:
      (totals.currentTasks - totals.previousTasks) / totals.previousTasks,
  };
};

// Convert tasks to opportunity periods (just a type conversion)
export const convertTasksToOpportunityPeriods = (
  tasks: { [key: string]: HygieneTaskProduction },
  estimatedValues: { [key in THygieneTaskType]: { value: number } },
  isCounts?: boolean,
): { [key in THygieneTaskType]: THygieneTaskPeriod } => {
  const taskCodes = Object.keys(tasks) as THygieneTaskCode[];
  const taskTypes = taskCodesToTypes(taskCodes);

  return taskTypes.reduce(
    (acc, taskType) => {
      // Find the code for this taskType
      const code = TYPE_TO_TASK_CODE[taskType];
      const value = tasks[code];
      const estimatedValue = estimatedValues[taskType]?.value || 0;
      acc[taskType] = value
        ? {
            current: isCounts
              ? value.current.tasksSuggested
              : value.current.tasksSuggested * estimatedValue,
            previous: isCounts
              ? value.previous.tasksSuggested
              : value.previous.tasksSuggested * estimatedValue,
            rateChange: 0,
          }
        : {
            current: 0,
            previous: 0,
            rateChange: 0,
          };
      return acc;
    },
    {} as { [key in THygieneTaskType]: THygieneTaskPeriod },
  );
};

// Convert tasks to production periods (just a type conversion)
export const convertTasksToTaskPeriods = (
  tasks: { [key: string]: HygieneTaskProduction },
  isCounts?: boolean,
): { [key in THygieneTaskType]: THygieneTaskPeriod } => {
  const taskCodes = Object.keys(tasks) as THygieneTaskCode[];
  const taskTypes = taskCodesToTypes(taskCodes);

  return taskTypes.reduce(
    (acc, taskType) => {
      // Find the code for this taskType
      const code = TYPE_TO_TASK_CODE[taskType];
      const value = tasks[code];
      acc[taskType] = value
        ? {
            current: isCounts
              ? value.current.tasksCompleted
              : value.current.tasksCompletedValue,
            previous: isCounts
              ? value.previous.tasksCompleted
              : value.previous.tasksCompletedValue,
            rateChange: 0,
          }
        : {
            current: 0,
            previous: 0,
            rateChange: 0,
          };
      return acc;
    },
    {} as { [key in THygieneTaskType]: THygieneTaskPeriod },
  );
};

const createEmptyTaskPeriod = (): THygieneTaskPeriod => ({
  current: 0,
  previous: 0,
  rateChange: 0,
});

const createEmptyTasks = (
  taskTypes: THygieneTaskType[],
): {
  [key in THygieneTaskType]: THygieneTaskPeriod;
} => {
  return taskTypes.reduce(
    (acc, taskType) => {
      acc[taskType as THygieneTaskType] = createEmptyTaskPeriod();
      return acc;
    },
    {} as { [key in THygieneTaskType]: THygieneTaskPeriod },
  );
};
